/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useContext, useEffect, useRef } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { theme, withProp } from 'styled-tools';

import Box from '@ui/box/Box.tsx';
import MapNavButton from '@ui/buttons/MapNavButton.tsx';
import Icon from '@ui/icons/Icon.tsx';
import Text from '@ui/text/Text.tsx';
import Tooltip from '@ui/tooltip';
import { rgba } from '@utils/dom.ts';
import { focusRelativeToElement } from '@utils/focus.ts';

import useComponentFocused from '../../hooks/useComponentFocused.tsx';

const ArrowWrapper = styled.div`
  position: absolute;
  height: 17px;
  width: 17px;
  bottom: 11px;
  inset-inline-end: 0;
  transform: translateX(50%);
  filter: drop-shadow(0px 5px 4px rgba(0, 0, 0, 0.2));

  [dir='rtl'] & {
    transform: translateX(-50%);
  }
`;

const Arrow = styled.div`
  transform: rotate(-135deg);
  display: block;
  height: 100%;
  width: 100%;
  background-color: ${withProp(theme('colors.primaryText'), primaryText => `1px solid ${rgba(primaryText, 0.1)}`)};
  clip-path: polygon(0% 0%, 100% 100%, 0% 100%);
  border-radius: 0 0 0 2px;
  position: absolute;
  top: 0;
  z-index: 1;

  [dir='rtl'] & {
    transform: rotate(45deg);
    border-radius: 0 0 2px 0;
  }
`;

const ArrowBackground = styled(Arrow)`
  background: ${theme('colors.background')};
  z-index: 0;
`;

const MenuOptions = styled(Box)`
  background-color: ${theme('colors.background')};
  color: ${theme('colors.primaryText')};
  z-index: 1;
`;

const MenuOption = styled(Box).attrs({ as: 'button' })`
  cursor: pointer;
  border: none;
  background: none;
  && {
    padding: 10px 6px;
    margin: 2px;
  }

  &::after {
    content: ' ';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 1px;
    inset-inline-start: 0;
    background: ${withProp(theme('colors.primaryText'), primaryText => rgba(primaryText, 0.1))};
    z-index: -1; /* should be behind focus outline */
  }

  &:last-child::after {
    content: none;
  }

  &:focus-visible {
    outline-offset: -1.5px;
  }

  &:hover {
    background-color: ${withProp(theme('colors.primaryText'), primaryText => rgba(primaryText, 0.1))};
  }
`;

const StyledMenuIcon = styled(Icon)`
  margin-inline-end: 8px;
`;

const mergeRefs = (...refs: Array<React.Ref<HTMLElement> | undefined | null>) => {
  const filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) {
    return null;
  }
  if (filteredRefs.length === 0) {
    return filteredRefs[0];
  }
  return (inst: HTMLElement | null) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLElement | null>).current = inst;
      }
    }
  };
};

interface MenuItem {
  iconId: string;
  label: string;
  lang?: string;
  onClick: () => void;
}

interface SubmenuProps {
  ref?: React.Ref<HTMLElement>;
  menuItems: MenuItem[];
  tooltipContent?: string;
  onOpen?: () => void;
  'data-testid'?: string;
  [key: string]: unknown;
}

const Submenu = ({ ref, ...props }: SubmenuProps) => {
  const themeContext = useContext(ThemeContext) as Record<string, any>;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLElement | null>(null);
  const { ref: componentFocusedRef, isComponentFocused, setIsComponentFocused } = useComponentFocused(true);
  const {
    menuItems,
    tooltipContent = 'View Menu',
    onOpen,
    'data-testid': dataTestId = 'Submenu',
    ...omittedProps
  } = props;

  useEffect(() => {
    if (!isComponentFocused && isMenuOpen) {
      setIsMenuOpen(false);
    } // if the outside click was detected, but menu was open, hide it
  }, [isComponentFocused]);

  const openMenu = () => {
    if (!isMenuOpen) {
      setIsMenuOpen(true);
      setIsComponentFocused(true);
      onOpen?.();
    }
  };

  const closeMenu = ({ focusButton = true } = {}) => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
      setIsComponentFocused(false);
      if (focusButton && buttonRef.current) {
        buttonRef.current.focus();
      }
    }
  };

  const toggleSubmenu = () => {
    if (isMenuOpen) {
      closeMenu({ focusButton: true });
    } else {
      openMenu();
    }
  };

  const onItemClick = (item: MenuItem) => {
    item.onClick();
    closeMenu({ focusButton: true });
  };

  // When menu opens set active index to first item and focus it
  useEffect(() => {
    if (isMenuOpen) {
      setActiveIndex(0);
    }
  }, [isMenuOpen]);

  // Focus currently active menu item when activeIndex changes
  useEffect(() => {
    if (isMenuOpen && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].focus();
    }
  }, [activeIndex, isMenuOpen]);

  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        openMenu();
        break;
      case 'Enter':
      case ' ': // space
        // Let default click happen which will toggle & focus first item via effect
        break;
      default:
        break;
    }
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (!isMenuOpen) {
      return;
    }
    const max = menuItems.length - 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => (prev + 1 > max ? 0 : prev + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => (prev - 1 < 0 ? max : prev - 1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(max);
        break;
      case 'Enter':
      case ' ': // space
        e.preventDefault();
        onItemClick(menuItems[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        closeMenu({ focusButton: true });
        break;
      case 'Tab': {
        // Move focus relative to the button (not the menuitem DOM position), then close.
        const direction = e.shiftKey ? -1 : 1;
        const moved = focusRelativeToElement(buttonRef.current, direction, menuRef.current);
        if (moved) {
          e.preventDefault();
          closeMenu({ focusButton: false });
        } else {
          // Could not resolve next focus target; allow native behavior.
          closeMenu({ focusButton: false });
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <Box display="flex" ref={mergeRefs(ref, componentFocusedRef)} data-testid={dataTestId} {...omittedProps}>
      {isMenuOpen && (
        <Box position="relative" display="flex">
          <Box
            id="submenu"
            role="menu"
            aria-label={tooltipContent}
            position="absolute"
            style={{ insetInlineEnd: '19px' }}
            bottom="-5px"
            display="flex"
            alignItems="flex-end"
            borderRadius="lg"
            boxShadow="md"
            onKeyDown={handleMenuKeyDown}
            ref={menuRef}
          >
            <MenuOptions
              data-testid="MenuOptions"
              display="flex"
              flexDirection="column"
              width="221px"
              borderRadius="lg"
              overflow="hidden"
            >
              {menuItems.map((item, index) => {
                const ariaLabel = `${item.iconId} ${item.label}`;
                return (
                  <MenuOption
                    key={index}
                    data-testid="MenuOption"
                    className="menuOption"
                    role="menuitem"
                    ref={(el: HTMLElement | null) => (itemRefs.current[index] = el)}
                    onClick={() => onItemClick(item)}
                    display="flex"
                    position="relative"
                    alignItems="center"
                    cursor="pointer"
                    tabIndex={activeIndex === index ? 0 : -1}
                    aria-label={!item.lang && item.iconId && item.label ? ariaLabel : undefined}
                    lang={item.lang}
                  >
                    <StyledMenuIcon name={item.iconId} iconSize="md" fillColor={themeContext.colors.primaryText} />
                    <Text fontSize="md">{item.label}</Text>
                  </MenuOption>
                );
              })}
            </MenuOptions>
            <ArrowWrapper>
              <Arrow />
              <ArrowBackground />
            </ArrowWrapper>
          </Box>
        </Box>
      )}
      <Tooltip className="" text={tooltipContent} cancelTooltip={isMenuOpen} arrowPosition="left">
        <MapNavButton
          ref={buttonRef}
          onClick={() => {
            // If already open let toggle close; if closed openMenu ensures first item focus via effect
            if (isMenuOpen) {
              toggleSubmenu();
            } else {
              openMenu();
            }
          }}
          iconName="submenu"
          degreesOfIconRotation={0}
          aria-label={tooltipContent}
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
          aria-controls="submenu"
          onKeyDown={handleButtonKeyDown}
        />
      </Tooltip>
    </Box>
  );
};

Submenu.displayName = 'Submenu';

export default Submenu;
